import axios from 'axios';
import { devtools } from 'zustand/middleware';
import {
  IComment,
  ICommentID,
  type CommentGroupChange,
} from '@shared/models/comment.model';
import equal from 'fast-deep-equal';
import { ProjectID } from '@shared/models/project.model';
import { getErrorMessage, updateResourceLocally } from '../store.helpers';
import { UpdateClientData, UpdateClientAction } from '../store.model';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

/** Drops a late `/comments` response when a newer project fetch has started. */
let latestCommentsFetchGeneration = 0;

type Error = { message: string; id?: string };

type UpdateCommentClientData = UpdateClientData<IComment> & {
  options?: Partial<Pick<CommentsState, 'isLoading' | 'editingId' | 'error'>>;
};

export interface CommentsState {
  isLoading: boolean;
  error: Error | undefined;
  comments: IComment[];

  fetchComments: (id: ProjectID) => Promise<void>;
  createComment: (comment: IComment, errorId: string) => Promise<void>;
  updateComment: (comment: IComment) => Promise<void>;
  changeCommentGroup: (change: CommentGroupChange) => Promise<void>;
  applyCommentGroup: (comments: IComment[]) => void;
  deleteComment: (id: ICommentID) => Promise<void>;
  restoreComment: (id: ICommentID) => Promise<void>;

  /** Util to update comments on client only */
  updateCommentsLocally: (args: UpdateCommentClientData) => void;

  scrollCommentsIntoView: boolean;
  setScrollCommentsIntoView: (value: boolean) => void;

  editingId: ICommentID | undefined;
  setEditingId: (id?: ICommentID) => void;
}

export const useCommentsStore = createWithEqualityFn<CommentsState>()(
  devtools(
    (set, get) => ({
      editingId: undefined,
      error: undefined,
      isLoading: false,
      scrollCommentsIntoView: false,
      comments: [],

      fetchComments: async (projectId) => {
        const fetchGeneration = ++latestCommentsFetchGeneration;
        set(() => ({ error: undefined, isLoading: true, comments: [] }));

        try {
          const { data } = await axios.get<IComment[]>('/comments', {
            params: { projectId },
          });

          // A newer project load owns the list; this response must not replace it.
          if (fetchGeneration !== latestCommentsFetchGeneration) return;

          // Compare against the cleared list, not the one before the fetch:
          // skipping an unchanged refetch would leave the thread empty.
          if (!equal(get().comments, data)) {
            set(() => ({ comments: data }));
          }
          set(() => ({ isLoading: false }));
        } catch {
          // A late failure must not hide the newer project's comments.
          if (fetchGeneration !== latestCommentsFetchGeneration) return;

          const message = getErrorMessage('get', 'comments');

          set(() => ({ error: { message }, isLoading: false }));
          // DataLoaderProxy does not catch this fetch; the comments UI reads `error`.
        }
      },

      createComment: async (comment, errorId) => {
        const { updateCommentsLocally } = get();

        set(() => ({ error: undefined, isLoading: true }));

        try {
          const { data } = await axios.post<IComment>('/comments', {
            ...comment,
            element_id: comment.element_id ?? null,
          });
          updateCommentsLocally({
            action: UpdateClientAction.Add,
            itemOrId: data,
          });
        } catch {
          const message = getErrorMessage('create', 'comment');

          set(() => ({ error: { id: errorId, message }, isLoading: false }));

          throw new Error(message);
        }
      },

      updateComment: async (comment) => {
        const { updateCommentsLocally } = get();

        updateCommentsLocally({
          action: UpdateClientAction.Update,
          itemOrId: comment,
          options: { isLoading: true },
        });

        try {
          const { data } = await axios.put<IComment>('/comments', comment);
          updateCommentsLocally({
            action: UpdateClientAction.Update,
            itemOrId: data,
          });
        } catch {
          const message = getErrorMessage('update', 'comment');

          updateCommentsLocally({
            options: { error: { id: comment.id, message } },
          });

          throw new Error(message);
        }
      },

      changeCommentGroup: async (change) => {
        const { data } = await axios.put<IComment[]>('/comments/group', change);
        get().applyCommentGroup(data);
      },
      applyCommentGroup: (changed) => {
        const ids = new Set(changed.map((comment) => comment.id));
        set(({ comments }) => ({
          comments: [
            ...comments.filter((comment) => !ids.has(comment.id)),
            ...changed.filter((comment) => !comment.deleted_at),
          ],
        }));
      },

      restoreComment: async (id) => {
        const { data } = await axios.post<IComment>(`/comments/${id}/restore`);
        get().updateCommentsLocally({
          action: UpdateClientAction.Add,
          itemOrId: data,
        });
      },

      deleteComment: async (id) => {
        const { updateCommentsLocally } = get();

        set(() => ({ error: undefined, isLoading: true }));

        try {
          await axios.delete(`/comments/${id}`);
          updateCommentsLocally({
            action: UpdateClientAction.Remove,
            itemOrId: id,
          });
        } catch {
          const message = getErrorMessage('delete', 'comment');

          set(() => ({ isLoading: false, error: { message } }));

          throw new Error(message);
        }
      },

      updateCommentsLocally: (data) => {
        const { comments, editingId: currentEditingId } = get();

        if (data.action) {
          const { collection } = updateResourceLocally({
            ...data,
            collection: comments,
          });

          const {
            isLoading = false,
            editingId = currentEditingId,
            error,
          } = data.options ?? {};

          set(() => ({
            isLoading,
            editingId,
            error,
            comments: (collection ?? comments).map((comment) => {
              const item = data.itemOrId;
              if (
                data.action !== UpdateClientAction.Add ||
                typeof item !== 'object' ||
                !item ||
                item.id !== comment.id
              )
                return comment;
              // Persisted comments (including Undo and socket restores) keep their history position.
              return {
                ...comment,
                created_at: item.created_at || comment.created_at,
                updated_at: item.updated_at || comment.updated_at,
              };
            }),
          }));
        }
      },

      setScrollCommentsIntoView: (value) => {
        if (get().scrollCommentsIntoView !== value) {
          set(() => ({ scrollCommentsIntoView: value }));
        }
      },

      setEditingId: (id) => {
        if (get().editingId !== id) {
          set(() => ({ editingId: id }));
        }
      },
    }),

    { name: 'comments' },
  ),
  shallow,
);
